/**
 * TaskReview — Self-contained proposed-changes card with built-in input.
 *
 * When interrupted for task_review: renders toggles + text input + Continue button.
 * The whole thing replaces the normal Composer during review.
 *
 * When pending tasks exist but no interrupt (agent running): read-only card.
 * When no pending tasks: hidden.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState, type FC, type ReactNode } from 'react';
import { useThread } from '@assistant-ui/react';
import { useLangGraphInterruptState, useLangGraphSendCommand } from '@assistant-ui/react-langgraph';
import {
  CheckIcon,
  BanIcon,
  XIcon,
  LoaderIcon,
  ClockIcon,
  PanelRightOpenIcon,
  ArrowUpIcon,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';
import { useTasks, type Task } from '@/client/lib/tasksContext';
import { useExternalAssets } from '@/client/lib/assetsContext';
import { cn } from '@/client/lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TaskDecision = 'approved' | 'pending' | 'rejected';

// ---------------------------------------------------------------------------
// Status Icon (same style as ProgressTracker step indicators)
// ---------------------------------------------------------------------------

function StatusIcon({ status }: { status: Task['status'] }) {
  if (status === 'completed') {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-success text-success-foreground">
        <CheckIcon className="size-2.5" strokeWidth={3} />
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
        <XIcon className="size-2.5" strokeWidth={3} />
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-destructive bg-background">
        <BanIcon className="size-2.5 text-destructive" />
      </span>
    );
  }
  if (status === 'approved') {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-primary bg-background shadow-[0_0_0_2px_hsl(262_60%_55%/0.1)]">
        <LoaderIcon className="size-2.5 animate-spin text-primary" />
      </span>
    );
  }
  // pending
  return (
    <span className="flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-warning bg-background">
      <ClockIcon className="size-2.5 text-warning" />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Toggle Button (3-state: approve / awaiting / reject)
// ---------------------------------------------------------------------------

function ToggleButton({
  active,
  variant,
  icon: Icon,
  title,
  onClick,
}: {
  active: boolean;
  variant: 'approve' | 'pending' | 'reject';
  icon: FC<{ className?: string }>;
  title: string;
  onClick: () => void;
}) {
  const activeStyles = {
    approve: 'bg-emerald-50 border-emerald-400 text-emerald-600',
    pending: 'bg-amber-50 border-amber-400 text-amber-600',
    reject: 'bg-red-50 border-red-400 text-red-600',
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'flex size-7 cursor-pointer items-center justify-center rounded-md border-[1.5px] transition-all duration-150',
            active
              ? activeStyles[variant]
              : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/40 hover:bg-muted/50',
          )}
        >
          <Icon className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {title}
      </TooltipContent>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Task Row
// ---------------------------------------------------------------------------

function TaskRow({
  task,
  decision,
  onDecision,
  onViewDetail,
  isLast,
}: {
  task: Task;
  decision?: TaskDecision;
  onDecision?: (decision: TaskDecision) => void;
  onViewDetail: () => void;
  isLast: boolean;
}) {
  return (
    <li className="relative flex items-center gap-2.5 py-1.5">
      {/* Connector line */}
      {!isLast && (
        <div className="absolute -bottom-[8px] left-[7.5px] top-[50%] w-px bg-border" aria-hidden />
      )}

      {/* Status icon */}
      <div className="relative z-10">
        <StatusIcon status={task.status} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-[13px] font-medium leading-snug',
            task.status === 'rejected' && 'line-through text-muted-foreground',
            task.status === 'completed' && 'text-foreground',
            task.status === 'failed' && 'text-foreground',
            task.status === 'pending' && 'text-foreground',
            task.status === 'approved' && 'shimmer shimmer-speed-80 text-foreground',
          )}
        >
          {task.title}
        </span>
        {task.status === 'failed' && task.result && (
          <p className="mt-0.5 text-[11px] leading-snug text-destructive">
            {task.result}
          </p>
        )}
        {task.status === 'rejected' && task.result && (
          <p className="mt-0.5 text-[11px] leading-snug italic text-muted-foreground">
            {task.result}
          </p>
        )}
      </div>

      {/* Right side: toggles for pending tasks + view button */}
      {task.status === 'pending' && onDecision ? (
        <div className="flex shrink-0 gap-1">
          <ToggleButton
            active={decision === 'approved'}
            variant="approve"
            icon={CheckIcon}
            title="Approve"
            onClick={() => onDecision(decision === 'approved' ? 'pending' : 'approved')}
          />
          <ToggleButton
            active={decision === 'pending' || !decision}
            variant="pending"
            icon={ClockIcon}
            title="Awaiting"
            onClick={() => onDecision('pending')}
          />
          <ToggleButton
            active={decision === 'rejected'}
            variant="reject"
            icon={BanIcon}
            title="Reject"
            onClick={() => onDecision(decision === 'rejected' ? 'pending' : 'rejected')}
          />
          <ToggleButton
            active={false}
            variant="pending"
            icon={PanelRightOpenIcon}
            title="View details"
            onClick={onViewDetail}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onViewDetail(); }}
          className="flex shrink-0 cursor-pointer items-center justify-center rounded-md border-[1.5px] border-border bg-background text-muted-foreground hover:border-muted-foreground/40 hover:bg-muted/50 size-7"
          title="View details"
        >
          <PanelRightOpenIcon className="size-3.5" />
        </button>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function useIsTaskReviewInterrupt(): boolean {
  const interruptState = useLangGraphInterruptState();
  const interruptValue = interruptState?.value as Record<string, unknown> | undefined;
  return interruptValue?.type === 'task_review';
}

/** Whether the task review card should show interactive mode (toggles + input). */
export function useIsTaskReviewMode(): boolean {
  const { tasks } = useTasks();
  const isInterrupted = useIsTaskReviewInterrupt();
  const isRunning = useThread((s) => s.isRunning);
  const pendingTasks = tasks.filter((t) => t.status === 'pending');
  return pendingTasks.length > 0 && isInterrupted && !isRunning;
}

// ---------------------------------------------------------------------------
// Shared decisions context — syncs TaskReview and sidebar detail
// ---------------------------------------------------------------------------

type TaskDecisionsContextValue = {
  decisions: Map<string, TaskDecision>;
  setDecision: (taskId: string, decision: TaskDecision) => void;
  isReviewMode: boolean;
};

const TaskDecisionsContext = createContext<TaskDecisionsContextValue>({
  decisions: new Map(),
  setDecision: () => {},
  isReviewMode: false,
});

export function useTaskDecisions() {
  return useContext(TaskDecisionsContext);
}

export const TaskDecisionsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [decisions, setDecisions] = useState<Map<string, TaskDecision>>(new Map());
  const isReviewMode = useIsTaskReviewMode();

  const setDecision = useCallback((taskId: string, decision: TaskDecision) => {
    setDecisions((prev) => {
      const next = new Map(prev);
      next.set(taskId, decision);
      return next;
    });
  }, []);

  // Clear decisions when leaving review mode
  useEffect(() => {
    if (!isReviewMode) {
      setDecisions(new Map());
    }
  }, [isReviewMode]);

  return (
    <TaskDecisionsContext.Provider value={{ decisions, setDecision, isReviewMode }}>
      {children}
    </TaskDecisionsContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// TaskReview — full self-contained card
// ---------------------------------------------------------------------------

export const TaskReview: FC = () => {
  const sendCommand = useLangGraphSendCommand();
  const { tasks } = useTasks();
  const { setPanelCollapsed, setPanelTab } = useExternalAssets();
  const isRunning = useThread((s) => s.isRunning);
  const { decisions, setDecision, isReviewMode } = useTaskDecisions();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const pendingTasks = tasks.filter((t) => t.status === 'pending');

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const updatedTasks = tasks.map((t) => {
        const decision = decisions.get(t.id);
        if (!decision || decision === 'pending') return t;
        return { ...t, status: decision };
      });

      await sendCommand({
        resume: {
          tasks: updatedTasks,
          message: message.trim() || null,
        } as unknown as string,
      });
    } finally {
      setIsSubmitting(false);
      setMessage('');
    }
  }, [tasks, decisions, message, sendCommand]);

  const handleViewDetail = useCallback(
    (taskId: string) => {
      setPanelCollapsed(false);
      setPanelTab(`task:${taskId}`);
    },
    [setPanelCollapsed, setPanelTab],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        if (!isSubmitting) {
          void handleSubmit();
        }
      }
    },
    [handleSubmit, isSubmitting],
  );

  // Only show when pending tasks exist
  if (pendingTasks.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-2xl border border-input bg-background transition-shadow has-[textarea:focus-visible]:border-[hsl(262_60%_55%)] has-[textarea:focus-visible]:ring-2 has-[textarea:focus-visible]:ring-[hsl(262_60%_55%)]">
      {/* Header + task rows */}
      <div className="border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <span className={cn(
            'min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground',
            isRunning && 'shimmer shimmer-speed-80',
          )}>
            Proposed changes
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground">
            {pendingTasks.length} pending
          </span>
        </div>
      </div>

      <div className="px-3 pb-2 pt-1.5">
        <ul>
          {pendingTasks.map((task, i) => (
            <TaskRow
              key={task.id}
              task={task}
              decision={decisions.get(task.id)}
              onDecision={isReviewMode ? (d) => setDecision(task.id, d) : undefined}
              onViewDetail={() => handleViewDetail(task.id)}
              isLast={i === pendingTasks.length - 1}
            />
          ))}
        </ul>
      </div>

      {/* Input + send — only in review mode */}
      {isReviewMode && (
        <div className="flex items-end gap-2 border-t px-3 py-2">
          <textarea
            ref={inputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add feedback (optional)..."
            rows={1}
            className="min-h-[36px] max-h-24 flex-1 resize-none rounded-md bg-transparent px-2 py-2 text-sm outline-none placeholder:text-muted-foreground"
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => void handleSubmit()}
                className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[hsl(262_60%_55%)] text-white transition-colors hover:bg-[hsl(262_60%_45%)] disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Continue"
              >
                <ArrowUpIcon className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Continue
            </TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
};
